import { supabase } from "@/lib/supabase/supabaseClient";
import { store } from "@/lib/states/store";
import type {
  AiChatMessage,
  AiStreamEvent,
  AiToolCall,
  AddExerciseArgs,
  RemoveExerciseArgs,
  ReplaceExerciseArgs,
  UpdateExerciseArgs,
} from "./types";

type CurrentExercise = {
  ex_order: number;
  name: string;
  tracking_type: string;
  sets?: Array<{
    startRange?: number;
    endRange?: number;
    targetTimeSeconds?: number;
    targetDistanceMeters?: number;
  }>;
};

type StreamParams = {
  messages: AiChatMessage[];
  currentWorkout: CurrentExercise[];
  onEvent: (event: AiStreamEvent) => void;
  signal?: AbortSignal;
};

const isAbortError = (err: unknown): boolean =>
  err instanceof Error && err.name === "AbortError";

// Accumulates tool call deltas from OpenAI's streaming format
type PartialToolCall = {
  index: number;
  id: string;
  name: string;
  argsJson: string;
};

const parseToolCallArgs = (
  name: string,
  argsJson: string,
): AiToolCall | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(argsJson);
  } catch {
    return null;
  }

  if (name === "add_exercise") {
    return { name: "add_exercise", args: parsed as AddExerciseArgs };
  }
  if (name === "remove_exercise") {
    return { name: "remove_exercise", args: parsed as RemoveExerciseArgs };
  }
  if (name === "replace_exercise") {
    return { name: "replace_exercise", args: parsed as ReplaceExerciseArgs };
  }
  if (name === "update_exercise") {
    return { name: "update_exercise", args: parsed as UpdateExerciseArgs };
  }
  return null;
};

export async function streamWorkoutBuilder({
  messages,
  currentWorkout,
  onEvent,
  signal,
}: StreamParams): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    onEvent({ type: "error", message: "Not authenticated" });
    return;
  }

  const orgId = store.getState().session.org?.id;
  if (!orgId) {
    onEvent({ type: "error", message: "No organisation selected" });
    return;
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_PB_KEY as string;

  // Only send role + content to the backend (no toolCalls metadata).
  // Filter out chips-only messages that have empty content.
  const apiMessages = messages
    .filter((m) => m.content.trim().length > 0)
    .map((m) => ({ role: m.role, content: m.content }));

  let response: Response;
  try {
    response = await fetch(
      `${supabaseUrl}/functions/v1/ai-workout-builder`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: anonKey,
          "x-org-id": orgId,
        },
        body: JSON.stringify({ messages: apiMessages, currentWorkout }),
        signal,
      },
    );
  } catch (err) {
    if (isAbortError(err)) return;
    onEvent({
      type: "error",
      message: err instanceof Error ? err.message : "Network error",
    });
    return;
  }

  if (!response.ok) {
    let errorMessage = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (typeof body.error === "string") errorMessage = body.error;
    } catch {
      // ignore
    }
    onEvent({ type: "error", message: errorMessage });
    return;
  }

  if (!response.body) {
    onEvent({ type: "error", message: "No response body" });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let rawLineCount = 0;
  let textDeltaCount = 0;
  let toolCallChunkCount = 0;

  // Accumulate tool call fragments indexed by their position in the choices[0].delta.tool_calls array
  const partialToolCalls = new Map<number, PartialToolCall>();

  const processLine = (line: string) => {
    if (!line.startsWith("data: ")) return;
    const data = line.slice(6).trim();
    rawLineCount++;

    if (data === "[DONE]") {
      console.log("[ai-workout-builder] [DONE] received", {
        rawLines: rawLineCount,
        textDeltas: textDeltaCount,
        toolCallChunks: toolCallChunkCount,
        pendingToolCalls: partialToolCalls.size,
      });
      // Flush any remaining tool calls that didn't get a finish_reason chunk
      if (partialToolCalls.size > 0) {
        console.log("[ai-workout-builder] flushing remaining tool calls on [DONE]");
        for (const [, partial] of partialToolCalls) {
          console.log("[ai-workout-builder] flushing tool call", { name: partial.name, argsLength: partial.argsJson.length });
          const call = parseToolCallArgs(partial.name, partial.argsJson);
          if (call) {
            onEvent({ type: "tool_call_complete", call });
          } else {
            console.warn("[ai-workout-builder] failed to parse tool call", { name: partial.name, argsJson: partial.argsJson.slice(0, 200) });
          }
        }
        partialToolCalls.clear();
      }
      onEvent({ type: "done" });
      return;
    }

    let chunk: unknown;
    try {
      chunk = JSON.parse(data);
    } catch (e) {
      console.warn("[ai-workout-builder] JSON parse error on line", { data: data.slice(0, 100), error: String(e) });
      return;
    }

    const choice = (chunk as { choices?: unknown[] })?.choices?.[0] as
      | {
          delta?: {
            content?: string | null;
            tool_calls?: Array<{
              index: number;
              id?: string;
              function?: { name?: string; arguments?: string };
            }>;
          };
          finish_reason?: string | null;
        }
      | undefined;

    if (!choice) {
      console.warn("[ai-workout-builder] no choice in chunk", JSON.stringify(chunk).slice(0, 100));
      return;
    }

    const delta = choice.delta;

    // Text delta
    if (delta?.content) {
      textDeltaCount++;
      onEvent({ type: "text_delta", content: delta.content });
    }

    // Tool call deltas
    if (delta?.tool_calls) {
      toolCallChunkCount++;
      for (const tc of delta.tool_calls) {
        const idx = tc.index;
        if (!partialToolCalls.has(idx)) {
          console.log("[ai-workout-builder] new tool call started", { index: idx, name: tc.function?.name });
          partialToolCalls.set(idx, {
            index: idx,
            id: tc.id ?? "",
            name: tc.function?.name ?? "",
            argsJson: "",
          });
        }
        const partial = partialToolCalls.get(idx)!;
        if (tc.id) partial.id = tc.id;
        if (tc.function?.name && partial.name === "") partial.name = tc.function.name;
        if (tc.function?.arguments) partial.argsJson += tc.function.arguments;
      }
    }

    // When finish_reason is "tool_calls" or "stop", flush all accumulated tool calls
    if (
      choice.finish_reason === "tool_calls" ||
      choice.finish_reason === "stop"
    ) {
      console.log("[ai-workout-builder] finish_reason:", choice.finish_reason, "flushing", partialToolCalls.size, "tool calls");
      for (const [, partial] of partialToolCalls) {
        console.log("[ai-workout-builder] flushing tool call", { name: partial.name, argsLength: partial.argsJson.length });
        const call = parseToolCallArgs(partial.name, partial.argsJson);
        if (call) {
          onEvent({ type: "tool_call_complete", call });
        } else {
          console.warn("[ai-workout-builder] failed to parse tool call", { name: partial.name, argsJson: partial.argsJson.slice(0, 300) });
        }
      }
      partialToolCalls.clear();
    }
  };

  console.log("[ai-workout-builder] starting stream read");
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        console.log("[ai-workout-builder] stream reader done", { rawLines: rawLineCount });
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE lines
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        processLine(line.trim());
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      processLine(buffer.trim());
    }
  } catch (err) {
    if (isAbortError(err)) return;
    console.error("[ai-workout-builder] stream read error", err);
    onEvent({
      type: "error",
      message: err instanceof Error ? err.message : "Stream read error",
    });
  } finally {
    reader.releaseLock();
  }
}
